import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react'
import { ReportableStatKind, StatEventReport, StatSource } from '@/models/stats.ts'
import { trackStat } from '@/controller/statsTracking.ts'

/**
 * Which piece of content the surrounding view is showing. Every reported event
 * needs at least one of `drive` / `share` / `portfolio` / `project` /
 * `selection`, and that context is the same for the whole view — so it is
 * provided once instead of threaded through every component.
 *
 * No provider means no tracking: a view that does not declare a scope (the
 * portal, above all) reports nothing.
 */
export interface StatScope {
  source?: StatSource
  drive?: string
  share?: string
  portfolio?: string
  project?: string
  selection?: string
}

const StatScopeContext = createContext<StatScope | null>(null)

export function StatScopeProvider({ scope, children }: { scope: StatScope, children: ReactNode }) {
  const value = useMemo(
    () => scope,
    [scope.source, scope.drive, scope.share, scope.portfolio, scope.project, scope.selection],
  )
  return <StatScopeContext.Provider value={value}>{children}</StatScopeContext.Provider>
}

export function useStatScope(): StatScope | null {
  return useContext(StatScopeContext)
}

export type StatEventDetails = Omit<StatEventReport, 'kind' | 'source' | 'drive' | 'share' | 'portfolio' | 'project' | 'selection'>
  & Partial<Pick<StatEventReport, 'source' | 'drive' | 'share' | 'portfolio' | 'project' | 'selection'>>

/**
 * Reports an event in the current scope. Outside a scope it does nothing, so
 * callers never have to check where they are rendered.
 */
export function useTrackStat(): (kind: ReportableStatKind, details?: StatEventDetails) => void {
  const scope = useStatScope()

  return useCallback((kind: ReportableStatKind, details?: StatEventDetails) => {
    if (!scope) return
    const event: StatEventReport = { ...scope, ...details, kind }
    // Every event needs at least one of these; a scope whose id has not arrived
    // yet (or whose content is not tracked at all) reports nothing.
    if (!event.drive && !event.share && !event.portfolio && !event.project && !event.selection) return
    trackStat(event)
  }, [scope])
}
